import { EWFileDropzoneArea } from '@extendedWarranty/components/EWFileDropzoneArea';
import { useExtendedWarrantyContext } from '@extendedWarranty/contexts/ExtendedWarrantyContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { alpha, CircularProgress, IconButton, Typography } from '@mui/material';
import { toaster } from '@utils/common';
import { useCallback } from 'react';

export const EWFileUpload = () => {
    const {
        formik: { values, setFieldValue, errors },
    } = useExtendedWarrantyContext();

    const removeFile = useCallback(
        (index: number) => {
            const changedFiles = values.extendedWarrantyFiles;

            if (!changedFiles) toaster.failure({ msg: 'An unexpected error has occured' });

            changedFiles?.splice(index, 1);
            return setFieldValue('extendedWarrantyFiles', changedFiles);
        },
        [values, setFieldValue]
    );

    const getFileSize = (file: File) => {
        let size = file.size;
        const fSExt = ['Bytes', 'KB', 'MB', 'GB'];
        let i = 0;
        while (size > 900) {
            size /= 1024;
            i++;
        }
        return Math.round(size * 100) / 100 + ' ' + fSExt[i];
    };

    return (
        <div className="flex flex-col md:flex-row gap-14">
            <EWFileDropzoneArea className="w-full md:w-1/2 w-grow" />
            <div className="flex flex-col w-full md:w-1/2 h-full min-h-28 max-h-[343px] flex-grow gap-4">
                <div className="flex flex-col gap-2">
                    <Typography variant="h5" color="black" fontWeight={700}>
                        Uploading
                    </Typography>
                    <hr className="max-w-[259px] min-w-24 w-full" />
                </div>
                <div className="flex flex-col gap-2 overflow-auto">
                    {values.extendedWarrantyFiles &&
                        values.extendedWarrantyFiles.length > 0 &&
                        values.extendedWarrantyFiles.map((fileInfo, i) => {
                            const hasError =
                                Array.isArray(errors.extendedWarrantyFiles) &&
                                !!values.extendedWarrantyFiles &&
                                !!errors.extendedWarrantyFiles?.[i]?.error;
                            const status = fileInfo.isLoading
                                ? 'Loading'
                                : hasError
                                ? 'Failed'
                                : values.extendedWarrantyFiles?.[i]?.error === ''
                                ? 'Complete'
                                : '';

                            return (
                                <div id={`file-${i}`} key={`${fileInfo.name}`} className="w-full">
                                    <div className="flex items-center gap-2">
                                        <UploadFileIcon color="primary" className="mr-2 md:mr-7" />
                                        <div className="flex flex-col gap-1 line-clamp-1">
                                            <Typography color="neutral-800" fontSize={'medium'} className="truncate">
                                                {fileInfo.name}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: (theme) => alpha(theme.palette.black.main, 0.6) }}
                                                className="flex gap-2.5 items-center truncate"
                                            >
                                                <span>{getFileSize(fileInfo.file)}</span>
                                                {!!status ? <span>•</span> : null}
                                                <Typography color={hasError ? 'error' : 'inherit'}>{status}</Typography>
                                            </Typography>
                                        </div>
                                        <div className="ml-auto flex items-center">
                                            <IconButton onClick={() => removeFile(i)}>
                                                <DeleteIcon />
                                            </IconButton>
                                            {fileInfo.isLoading ? <CircularProgress size={40} color="primary" /> : null}
                                            {status === 'Complete' ? (
                                                <CheckCircleIcon color="success" fontSize="large" />
                                            ) : null}
                                        </div>
                                    </div>
                                    {hasError ? (
                                        <div className="w-full flex py-2.5 px-3 gap-4 bg-jasper rounded-lg my-5">
                                            <ErrorOutlineIcon color="white" />
                                            <div className="flex flex-col gap-1">
                                                <Typography
                                                    variant="body1"
                                                    color="whitesmoke"
                                                    fontWeight={600}
                                                    className="md:line-clamp-1"
                                                >
                                                    Incorrect Serial Numbers
                                                </Typography>
                                                <Typography
                                                    color="whitesmoke"
                                                    fontWeight={500}
                                                    variant="body2"
                                                    className="line-clamp-2"
                                                >
                                                    {values.extendedWarrantyFiles?.[i]?.error}
                                                </Typography>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                </div>
            </div>
        </div>
    );
};
